import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import useRealtimeAudioCall from '../Hooks/realtimeAudioCall/useRealtimeAudioCall';
import { A2AApiProvider, useA2AApi } from '../Services/A2AApiProvider';
import AudioMessageRecorder from './Chat/AudioMessageRecorder';
import AudioMessagesPlayer from './Chat/AudioMessagesPlayer';
import './TestAgentModal.css';

interface Message {
  from: 'user' | 'assistant';
  time: string;
  rawText: string;
  audio?: {
    url: string;
    histogram: number[];
    playbackTime: number;
  } | null;
  isVoiceCall?: boolean;
}

const formatTime = (timestamp?: Date | null) =>
  new Date(timestamp || Date.now()).toLocaleTimeString().slice(0, 8);

interface VoiceProfileTestChatContentProps {
  voiceProfileId: string;
  assistantKey: string;
  sessionKey?: string;
  initialMessages?: Message[];
}

function VoiceProfileTestChatContent({
  voiceProfileId,
  assistantKey,
  sessionKey,
  initialMessages,
}: VoiceProfileTestChatContentProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages ?? []);
  const [isStreaming, setIsStreaming] = useState(false);
  const chatBoxRef = useRef<HTMLDivElement>(null);

  const [audioMessageSubmitted, setAudioMessageSubmitted] = useState(false);
  const [pendingAudioData, setPendingAudioData] = useState<any>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  // Reset messages when switching sessions
  useEffect(() => {
    setMessages(initialMessages ?? []);
  }, [sessionKey, initialMessages]);

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const { sendMessage } = useA2AApi();

  // Realtime audio call hook — pass voiceProfileId and sessionKey to session.create
  const { isCallActive, isConnecting, toggleCall } = useRealtimeAudioCall(assistantKey, {
    voiceProfileId,
    sessionKey,
    onTranscriptMessage: useCallback((text: string) => {
      const time = new Date();
      setMessages((prev) => [
        ...prev,
        {
          from: 'assistant',
          time: formatTime(time),
          rawText: text,
          isVoiceCall: true,
        },
      ]);
    }, []),
  });

  useEffect(() => {
    setIsButtonEnabled(!!pendingAudioData);
  }, [pendingAudioData]);

  const handleSend = useCallback(async () => {
    if (!pendingAudioData) return;

    const { audioDataUrl, histogram = [], playback_time = 0 } = pendingAudioData.jsonData ?? {};

    const [meta, base64] = audioDataUrl?.split(',') ?? ['', ''];
    const mimeType = meta.match(/^data:(.*);base64$/)?.[1] ?? 'audio/mp4';

    const audio = {
      sizeMB: base64 ? base64.length / (1024 * 1024) : null,
      base64Preview: audioDataUrl,
      url: pendingAudioData.jsonData.file,
      name: 'audio',
      type: mimeType,
      histogram,
      playbackTime: playback_time,
    };

    const userMessage: Message = {
      from: 'user',
      time: formatTime(new Date()),
      rawText: '',
      audio: {
        url: audio.url,
        histogram: audio.histogram,
        playbackTime: audio.playbackTime,
      },
    };

    setMessages((prev) => [...prev, userMessage]);
    setPendingAudioData(null);
    setAudioMessageSubmitted(true);
    setIsButtonEnabled(false);
    setIsStreaming(true);

    const allFiles = [audio];
    const conversation = [...messagesRef.current, userMessage];

    let streamingAssistantIndex: number | null = null;
    let lastMessageId: string | null = null;

    try {
      const eventCallback = (eventMessage: any) => {
        let ev: any;
        try {
          ev = typeof eventMessage === 'string' ? JSON.parse(eventMessage) : eventMessage;
        } catch {
          return;
        }

        const statusEnvelope = ev?.result?.status ? ev.result : null;
        if (statusEnvelope) {
          if (statusEnvelope.status.state === 'submitted') return;

          const isFinal = statusEnvelope.final === true;
          const isCompleted = statusEnvelope.status.state === 'completed';

          const parts = statusEnvelope.status.message?.parts ?? [];
          const textParts = parts
            .map((p: any) => p?.root ?? p)
            .filter((p: any) => p?.kind === 'text')
            .map((p: any) => p.text || '')
            .join('\n');

          if (!textParts && !isFinal && !isCompleted) return;

          const messageId = statusEnvelope.status.message?.messageId || null;
          const assistantMessage: Message = {
            from: 'assistant',
            time: formatTime(new Date(statusEnvelope.status.timestamp || Date.now())),
            rawText: textParts,
          };

          setMessages((prev) => {
            if (streamingAssistantIndex !== null && messageId && messageId === lastMessageId) {
              const next = prev.slice();
              next[streamingAssistantIndex] = assistantMessage;
              return next;
            }
            streamingAssistantIndex = prev.length;
            lastMessageId = messageId;
            return [...prev, assistantMessage];
          });

          if (isFinal || isCompleted) setIsStreaming(false);
        }
      };

      await sendMessage(assistantKey, '', allFiles, conversation, eventCallback);
      setIsStreaming(false);
    } catch (error: any) {
      setIsStreaming(false);
      setMessages((prev) => [
        ...prev,
        {
          from: 'assistant',
          rawText: `⚠️ Error: ${error?.message || 'Failed to send audio message.'}`,
          time: formatTime(new Date()),
        },
      ]);
    }
  }, [pendingAudioData, assistantKey, sendMessage]);

  useLayoutEffect(() => {
    const el = chatBoxRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, isStreaming]);

  return (
    <div className="agent-test-modal-content">
      <div className="chat-box radius-yes border-yes p-2" ref={chatBoxRef}>
        {messages.map((msg, idx) => (
          <div key={idx}>
            <div className={`message ${msg.from}`}>
              <span className="timestamp">{msg.time}</span>
              <div className="test-message-content">
                {msg.audio && (
                  <div className="message-bubble user-msg">
                    <AudioMessagesPlayer
                      audioUrl={msg.audio.url}
                      histogramData={msg.audio.histogram || []}
                      playbackTime={msg.audio.playbackTime || 0}
                    />
                  </div>
                )}
                {msg.rawText && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ node, ...props }: any) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                          {props.children}
                        </a>
                      ),
                    }}
                  >
                    {msg.rawText}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          </div>
        ))}
        {isStreaming && (
          <div className="message assistant">
            <div className="test-message-content">
              <em>processing...</em>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="text-area-toolbar">
          <div className="chat-toolbar">
            <div className="chat-options">
              <AudioMessageRecorder
                onAudioRecorded={({ flags, jsonData }: any) => {
                  setPendingAudioData({ flags, jsonData });
                  setIsButtonEnabled(true);
                }}
                setAudioMessageSubmitted={setAudioMessageSubmitted}
                audioMessageSubmitted={audioMessageSubmitted}
              />
            </div>

            <div className="submit-button-container">
              <button
                className={`call-button ${isCallActive ? 'active' : ''} ${isConnecting ? 'connecting' : ''}`}
                onClick={toggleCall}
                disabled={isConnecting}
                title={isCallActive ? 'End voice call' : 'Start voice call'}
              >
                {isConnecting ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="32">
                      <animate
                        attributeName="stroke-dashoffset"
                        values="32;0"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                ) : isCallActive ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 6h12v12H6z" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                )}
              </button>

              <button
                className={`submit-button ${isButtonEnabled ? 'active' : ''}`}
                onClick={handleSend}
                disabled={!isButtonEnabled}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface VoiceProfileTestChatProps {
  voiceProfileId: string;
  assistantKey: string;
  sessionKey?: string;
  initialMessages?: Message[];
}

export type { Message as VoiceProfileChatMessage };

export default function VoiceProfileTestChat({
  voiceProfileId,
  assistantKey,
  sessionKey,
  initialMessages,
}: VoiceProfileTestChatProps) {
  return (
    <A2AApiProvider>
      <VoiceProfileTestChatContent
        voiceProfileId={voiceProfileId}
        assistantKey={assistantKey}
        sessionKey={sessionKey}
        initialMessages={initialMessages}
      />
    </A2AApiProvider>
  );
}
